import React, { useState, useTransition } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useLocalAuth } from '../../contexts/LocalAuthContext';

/**
 * Register page — same lg-* design language as the login screen.
 */
const LocalRegisterPage: React.FC = () => {
  const { register } = useLocalAuth();
  const navigate = useNavigate();

  const [form, setForm] = useState({ username: '', email: '', fullName: '', password: '', confirm: '' });
  const [error, setError] = useState<string | null>(null);
  const [isPending, startTransition] = useTransition();

  const canSubmit =
    form.username.trim().length >= 3 &&
    /.+@.+\..+/.test(form.email) &&
    form.fullName.trim().length >= 2 &&
    form.password.length >= 6 &&
    form.password === form.confirm;

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!canSubmit) {
      setError('Please complete every field correctly.');
      return;
    }
    startTransition(async () => {
      try {
        await register({
          username: form.username.trim(),
          email: form.email.trim(),
          fullName: form.fullName.trim(),
          password: form.password,
        });
        navigate('/home', { replace: true });
      } catch (err: any) {
        setError(err?.response?.data?.message || err.message || 'Registration failed.');
      }
    });
  };

  return (
    <div className="lg-root">
      <style>{`
        .lg-root {
          display: flex; height: 100vh; overflow: hidden;
          font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", "Inter", Roboto, sans-serif;
          --lg-dark: #0f172a; --lg-mid: #1e293b; --lg-teal: #14b8a6;
          --lg-teal-focus: rgba(20,184,166,0.15);
          --lg-slate-50:#f8fafc; --lg-slate-200:#e2e8f0;
          --lg-slate-400:#94a3b8; --lg-slate-500:#64748b;
          --lg-slate-700:#334155; --lg-slate-900:#0f172a;
          --lg-danger:#ef4444; --lg-danger-bg:#fef2f2; --lg-danger-border:#fecaca;
        }
        .lg-root * { box-sizing: border-box; }
        .lg-brand {
          width: 44%; flex-shrink: 0;
          background: linear-gradient(155deg, var(--lg-dark) 0%, var(--lg-mid) 100%);
          display: flex; flex-direction: column; padding: 2.5rem 3rem;
          position: relative; overflow: hidden;
          box-shadow: 8px 0 40px rgba(0,0,0,0.3); z-index: 1;
        }
        .lg-brand-logo { display:flex; align-items:center; flex-shrink:0; }
        .lg-graphic { flex:1; display:flex; align-items:center; justify-content:center; padding: 2rem 0; }
        .lg-graphic svg { width:100%; max-width:340px; }
        .lg-hub-ring   { animation: lg-ring-pulse 4s ease-in-out infinite; }
        .lg-hub-ring-2 { animation: lg-ring-pulse 4s ease-in-out infinite 1.5s; }
        .lg-hub-ring-3 { animation: lg-ring-pulse 4s ease-in-out infinite 0.8s; }
        @keyframes lg-ring-pulse { 0%,100% { opacity: 0.15; } 50% { opacity: 0.55; } }
        .lg-tagline-h { font-size: 1.75rem; font-weight: 700; color: #fff; line-height: 1.2; letter-spacing: -0.035em; margin: 0 0 0.625rem; }
        .lg-tagline-p { font-size: 0.875rem; color: rgba(255,255,255,0.38); margin: 0; }
        .lg-brand-footer { margin-top: 2rem; }
        .lg-version { font-size: 0.6875rem; color: rgba(255,255,255,0.18); font-weight: 500; letter-spacing: 0.03em; }

        .lg-form-panel {
          flex: 1; background: var(--lg-slate-50);
          background-image:
            linear-gradient(rgba(15,23,42,0.025) 1px, transparent 1px),
            linear-gradient(90deg, rgba(15,23,42,0.025) 1px, transparent 1px);
          background-size: 32px 32px;
          display: flex; align-items: center; justify-content: center;
          padding: 2.5rem 1.5rem; overflow-y: auto;
        }
        .lg-form-inner { width: 100%; max-width: 460px; animation: lg-enter 0.5s cubic-bezier(0.16,1,0.3,1); }
        @keyframes lg-enter { 0% { opacity:0; transform: translateY(8px); } 100% { opacity:1; transform: translateY(0); } }

        .lg-app-identity { display:flex; align-items:center; gap: 0.75rem; margin-bottom: 1.25rem; }
        .lg-app-badge { width: 2.25rem; height: 2.25rem; border-radius: 0.5rem; background: var(--lg-slate-900); color: #fff; display:flex; align-items:center; justify-content:center; font-weight: 700; font-size: 0.8125rem; }
        .lg-app-name { font-size: 0.9375rem; font-weight: 700; color: var(--lg-slate-900); }

        .lg-form-h1 { font-size: 1.5rem; font-weight: 700; color: var(--lg-slate-900); letter-spacing: -0.02em; margin: 0 0 0.375rem; }
        .lg-form-sub { font-size: 0.8125rem; color: var(--lg-slate-500); margin: 0 0 1.5rem; }

        .lg-row-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 0.75rem; }
        @media (max-width: 480px) { .lg-row-2 { grid-template-columns: 1fr; } }

        .lg-field { margin-bottom: 0.875rem; }
        .lg-label { display:block; font-size: 0.75rem; font-weight: 600; color: var(--lg-slate-700); letter-spacing: 0.01em; margin-bottom: 0.375rem; }
        .lg-input {
          width: 100%; height: 2.5rem; padding: 0 0.875rem;
          font-size: 0.875rem; color: var(--lg-slate-900);
          background: #fff; border: 1px solid var(--lg-slate-200);
          border-radius: 0.5rem; outline: none;
          transition: all 0.18s ease;
        }
        .lg-input:focus { border-color: var(--lg-teal); box-shadow: 0 0 0 3px var(--lg-teal-focus); }
        .lg-input.lg-err { border-color: var(--lg-danger); }
        .lg-field-help { font-size: 0.6875rem; color: var(--lg-slate-500); margin-top: 0.25rem; }
        .lg-field-err  { font-size: 0.6875rem; color: var(--lg-danger); margin-top: 0.25rem; }

        .lg-error-alert {
          display: flex; align-items: center; gap: 0.625rem;
          background: var(--lg-danger-bg); border: 1px solid var(--lg-danger-border);
          color: var(--lg-danger); padding: 0.625rem 0.875rem;
          border-radius: 0.5rem; margin-bottom: 1rem; font-size: 0.8125rem;
        }

        .lg-submit {
          width: 100%; height: 2.75rem; margin-top: 0.5rem;
          background: var(--lg-teal); color: #fff;
          border: none; border-radius: 0.5rem;
          font-size: 0.875rem; font-weight: 600;
          cursor: pointer; transition: all 0.18s ease;
          display:flex; align-items:center; justify-content:center; gap: 0.5rem;
        }
        .lg-submit:hover:not(:disabled) { background: #0f9b8e; box-shadow: 0 4px 14px rgba(20,184,166,0.3); }
        .lg-submit:disabled { opacity: 0.55; cursor: not-allowed; }
        .lg-spinner { width: 1rem; height: 1rem; border: 2px solid rgba(255,255,255,0.3); border-top-color: #fff; border-radius: 50%; animation: lg-spin 0.75s linear infinite; }
        @keyframes lg-spin { to { transform: rotate(360deg); } }

        .lg-form-footer {
          margin-top: 1.5rem; padding-top: 1.5rem;
          border-top: 1px solid var(--lg-slate-200);
          text-align: center; font-size: 0.8125rem; color: var(--lg-slate-500);
        }
        .lg-form-footer a { color: var(--lg-teal); font-weight: 600; text-decoration: none; }
        .lg-form-footer a:hover { text-decoration: underline; }

        @media (max-width: 900px) { .lg-brand { display: none; } }
      `}</style>

      <aside className="lg-brand">
        <div className="lg-brand-logo">
          <div style={{ display:'flex', alignItems:'center', gap:'0.625rem' }}>
            <div style={{ width: 36, height: 36, borderRadius: 8, background:'var(--lg-teal)', display:'flex', alignItems:'center', justifyContent:'center', color:'#fff', fontWeight:800 }}>TT</div>
            <span style={{ color:'#fff', fontSize:'1rem', fontWeight: 700 }}>Time Tracker</span>
          </div>
        </div>
        <div className="lg-graphic">
          <svg viewBox="0 0 240 240" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle className="lg-hub-ring"   cx="120" cy="120" r="100" stroke="rgba(20,184,166,0.35)" strokeWidth="1" />
            <circle className="lg-hub-ring-2" cx="120" cy="120" r="70"  stroke="rgba(20,184,166,0.4)"  strokeWidth="1" />
            <circle className="lg-hub-ring-3" cx="120" cy="120" r="40"  stroke="rgba(20,184,166,0.55)" strokeWidth="1" />
            <circle cx="120" cy="120" r="12" fill="rgba(20,184,166,0.85)" />
            <path d="M120 108v14l8 6" stroke="#fff" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </div>
        <h2 className="lg-tagline-h">Get started in a minute.</h2>
        <p className="lg-tagline-p">The first user you register becomes the workspace admin.</p>
        <div className="lg-brand-footer">
          <span className="lg-version">v0.1 · Local auth</span>
        </div>
      </aside>

      <main className="lg-form-panel">
        <div className="lg-form-inner">
          <div className="lg-app-identity">
            <div className="lg-app-badge">TT</div>
            <span className="lg-app-name">Time Tracker</span>
          </div>

          <h1 className="lg-form-h1">Create account</h1>
          <p className="lg-form-sub">First registration becomes Admin automatically.</p>

          <form onSubmit={submit} noValidate>
            {error && (
              <div className="lg-error-alert">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" /></svg>
                <span>{error}</span>
              </div>
            )}

            <div className="lg-row-2">
              <div className="lg-field">
                <label className="lg-label">Username</label>
                <input className="lg-input" value={form.username}
                  onChange={(e) => setForm({ ...form, username: e.target.value })}
                  autoComplete="username" autoFocus />
                <div className="lg-field-help">3+ characters</div>
              </div>
              <div className="lg-field">
                <label className="lg-label">Email</label>
                <input className="lg-input" type="email" value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  autoComplete="email" />
              </div>
            </div>

            <div className="lg-field">
              <label className="lg-label">Full name</label>
              <input className="lg-input" value={form.fullName}
                onChange={(e) => setForm({ ...form, fullName: e.target.value })}
                autoComplete="name" />
            </div>

            <div className="lg-row-2">
              <div className="lg-field">
                <label className="lg-label">Password</label>
                <input className="lg-input" type="password" value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  autoComplete="new-password" />
                <div className="lg-field-help">6+ characters</div>
              </div>
              <div className="lg-field">
                <label className="lg-label">Confirm password</label>
                <input
                  className={`lg-input ${form.confirm && form.confirm !== form.password ? 'lg-err' : ''}`}
                  type="password" value={form.confirm}
                  onChange={(e) => setForm({ ...form, confirm: e.target.value })}
                  autoComplete="new-password" />
                {form.confirm && form.confirm !== form.password && (
                  <div className="lg-field-err">Passwords don't match</div>
                )}
              </div>
            </div>

            <button className="lg-submit" type="submit" disabled={isPending || !canSubmit}>
              {isPending && <span className="lg-spinner" />}
              {isPending ? 'Creating…' : 'Create account'}
            </button>
          </form>

          <div className="lg-form-footer">
            Already have an account? <RouterLink to="/login">Sign in</RouterLink>
          </div>
        </div>
      </main>
    </div>
  );
};

export default LocalRegisterPage;
